import { Observable, Subject } from 'rxjs';

import { Portal, PortalHost } from '../portal/portal';
import { OverlayState } from './overlay-state';

/**
 * Reference to an overlay that has been created with the Overlay service.
 * Used to manipulate or dispose of said overlay.
 */
export class OverlayRef implements PortalHost {
  private _backdropElement: HTMLElement | null = null;
  private _backdropClick = new Subject<any>();

  constructor(
    private _portalHost: PortalHost,
    private _pane: HTMLElement,
    private _state: OverlayState,
  ) {}

  /** The overlay's HTML element */
  public get overlayElement(): HTMLElement {
    return this._pane;
  }

  /**
   * Attaches the overlay to a portal instance and adds the backdrop.
   * @param portal Portal instance to which to attach the overlay.
   * @returns The portal attachment result.
   */
  public attach<T extends Portal<any>>(portal: T): T extends Portal<infer R> ? R : never {
    const attachResult = this._portalHost.attach(portal);

    // Update the pane element with the given state configuration.
    this._updateStackingOrder();
    this.updateSize();
    this.updateDirection();
    this.updatePosition();

    this._pane.style.zIndex = `${this._state.zIndex}`;
    // Enable pointer events for the overlay pane element.
    this._togglePointerEvents(!this._state.disablePointerEvents);

    if (this._state.hasBackdrop) {
      this._attachBackdrop();
    }

    return attachResult;
  }

  /**
   * Detaches an overlay from a portal.
   * @returns Resolves when the overlay has been detached.
   */
  public detach(): Promise<any> {
    this.detachBackdrop();

    // When the overlay is detached, the pane element should disable pointer events.
    // This is necessary because otherwise the pane element will cover the page and disable
    // pointer events therefore. Depends on the position strategy and the applied pane boundaries.
    this._togglePointerEvents(false);

    return this._portalHost.detach();
  }

  /**
   * Cleans up the overlay from the DOM.
   */
  public dispose(): void {
    if (this._state.positionStrategy) {
      this._state.positionStrategy.dispose();
    }

    this.detachBackdrop();
    this._portalHost.dispose();
  }

  /**
   * Checks whether the overlay has been attached.
   */
  public hasAttached(): boolean {
    return this._portalHost.hasAttached();
  }

  /**
   * Returns an observable that emits when the backdrop has been clicked.
   */
  public backdropClick(): Observable<void> {
    return this._backdropClick.asObservable();
  }

  /**
   * Gets the current state config of the overlay.
   */
  public getState(): OverlayState {
    return this._state;
  }

  /** Updates the position of the overlay based on the position strategy. */
  public updatePosition(): void {
    if (this._state.positionStrategy) {
      this._state.positionStrategy.apply(this._pane, `${this._state.zIndex}`);
    }
  }

  /** Updates the size of the overlay based on the overlay config. */
  public updateSize(): void {
    if (this._state.width || this._state.width === 0) {
      this._pane.style.width = formatCssUnit(this._state.width);
    }

    if (this._state.height || this._state.height === 0) {
      this._pane.style.height = formatCssUnit(this._state.height);
    }

    if (this._state.minWidth || this._state.minWidth === 0) {
      this._pane.style.minWidth = formatCssUnit(this._state.minWidth);
    }

    if (this._state.minHeight || this._state.minHeight === 0) {
      this._pane.style.minHeight = formatCssUnit(this._state.minHeight);
    }
  }

  /** Detaches the backdrop (if any) associated with the overlay. */
  public detachBackdrop(): void {
    const backdropToDetach = this._backdropElement;

    if (backdropToDetach) {
      const finishDetach = () => {
        // It may not be attached to anything in certain cases (e.g. unit tests).
        if (backdropToDetach && backdropToDetach.parentNode) {
          backdropToDetach.parentNode.removeChild(backdropToDetach);
        }

        // It is possible that a new portal has been attached to this overlay since we started
        // removing the backdrop. If that is the case, only clear the backdrop reference if it
        // is still the same instance that we started to remove.
        if (this._backdropElement === backdropToDetach) {
          this._backdropElement = null;
        }
      };

      backdropToDetach.classList.remove('rt-overlay-backdrop-showing');
      backdropToDetach.classList.remove(this._state.backdropClass);
      backdropToDetach.addEventListener('transitionend', finishDetach);

      // If the backdrop doesn't have a transition, the `transitionend` event won't fire.
      // In this case we make it unclickable and we try to remove it after a delay.
      backdropToDetach.style.pointerEvents = 'none';

      setTimeout(finishDetach, 500);
    }
  }

  public updateState(state: OverlayState): void {
    this._state = state;
  }

  /** Updates the text direction of the overlay panel. **/
  private updateDirection(): void {
    this._pane.setAttribute('dir', this._state.direction);
  }

  /** Toggles the pointer events for the overlay pane element. */
  private _togglePointerEvents(enablePointer: boolean): void {
    this._pane.style.pointerEvents = enablePointer ? 'auto' : 'none';
  }

  /** Attaches a backdrop for this overlay. */
  private _attachBackdrop(): void {
    this._backdropElement = document.createElement('div');
    this._backdropElement.classList.add('rt-overlay-backdrop');
    this._backdropElement.classList.add(this._state.backdropClass);

    // Insert the backdrop before the pane in the DOM order,
    // in order to handle stacked overlays properly.
    this._pane.parentElement!.insertBefore(this._backdropElement, this._pane);

    // Forward backdrop clicks such that the consumer of the overlay can perform whatever
    // action desired when such a click occurs (usually closing the overlay).
    this._backdropElement.addEventListener('click', () => this._backdropClick.next(null));

    // Add class to fade-in the backdrop after one frame.
    requestAnimationFrame(() => {
      if (this._backdropElement) {
        this._backdropElement.classList.add('rt-overlay-backdrop-showing');
      }
    });
  }

  /**
   * Updates the stacking order of the element, moving it to the top if necessary.
   * This is required in cases where one overlay was detached, while another one,
   * that should be behind it, was destroyed. The next time both of them are opened,
   * the stacking will be wrong, because the detached element's pane will still be
   * in its original DOM position.
   */
  private _updateStackingOrder(): void {
    const paneLvl = parseInt(this._pane.dataset['overlayLevel']!, 10);
    const prevElement = <HTMLElement | undefined>this._pane.previousSibling;

    if (!prevElement) {
      return;
    }

    const prevLvl = prevElement ? parseInt(prevElement.dataset['overlayLevel']!, 10) : -1;

    if (prevLvl > -1 && paneLvl < prevLvl) {
      const firstOnLevelAbove = this._pane.parentElement!.querySelector(
        `[data-overlay-level="${prevLvl}"]`,
      );
      this._pane.parentElement!.insertBefore(this._pane, firstOnLevelAbove);
    }
  }
}

function formatCssUnit(value: number | string): string {
  return typeof value === 'string' ? value : `${value}px`;
}
